import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchJSON } from "@/lib/api";
import { Check, ChevronRight, Home, Printer } from "lucide-react";

interface Template {
  _id: string;
  templateId: string;
  name: string;
  type: 'bill' | 'invoice';
  isActive: boolean;
  htmlStructure: string;
  styles?: string;
  logoType: 'indianoil' | 'hp' | 'reliance' | 'bpcl' | 'custom';
}

const TEMPLATE_IMAGES: Record<string, string> = {
  indianoil_thermal: "/media__1779795793098.png",
  clean_metadata_receipt: "/media__1779795789866.png",
  hp_cash_bill: "/media__1779900738139.png",
  landscape_tax_invoice: "/media__1779796717645.png",
  portrait_gst_invoice: "/media__1779795804714.png"
};

const MOCK_PRINT_DATA = {
  orgName: "DELHI FUEL POINT STATION",
  orgGst: "07AAAAA1111A1Z1",
  orgContact: "+91 98765 43210",
  billNo: "CS-2026-9081",
  date: "2026-05-26",
  time: "17:45:00",
  product: "SPEED DIESEL",
  qty: "45.50",
  price: "94.20",
  total: "4286.10",
  cgst: "326.85",
  sgst: "326.85",
  discount: "0.00",
  mobile: "9812345678",
  indentNo: "IND-5521",
  customerName: "METRO TRAVELS CO",
  vehicleNo: "DL 01 GB 9922",
  employee: "Rajesh Kumar",
  customerGst: "07BBBBB2222B2Z2",
  customerMobile: "9876543210",
  customerAddress: "Sector 62, Noida, UP",
  taxableAmt: "3632.40",
  totalInWords: "Four Thousand Two Hundred Eighty Six Rupees and Ten Paise Only",
  bankAcc: "3902998877112",
  bankName: "State Bank of India",
  bankIfsc: "SBIN0001234"
};

export default function PrintTemplates() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedBillId, setSelectedBillId] = useState<string>("");
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>("");

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const res = await fetchJSON("/api/print-templates");
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        const errMsg = errJson?.error || `Server error (${res.status})`;
        setLoadError(errMsg);
        toast.error("Could not load print templates: " + errMsg);
        return;
      }
      const json = await res.json();
      if (json.success) {
        setTemplates(json.rows || []);
        
        // Find active defaults
        const activeBill = json.rows.find((t: Template) => t.type === "bill" && t.isActive);
        const activeInvoice = json.rows.find((t: Template) => t.type === "invoice" && t.isActive);
        
        if (activeBill) setSelectedBillId(activeBill.templateId);
        if (activeInvoice) setSelectedInvoiceId(activeInvoice.templateId);
      } else {
        const errMsg = json.error || "Failed to load print templates";
        setLoadError(errMsg);
        toast.error(errMsg);
      }
    } catch (err) {
      console.error(err);
      const errMsg = "Network error — could not reach the server";
      setLoadError(errMsg);
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const compileTemplateHtml = (template: Template, printData: any) => {
    let html = template.htmlStructure;
    const logoUrl = {
      indianoil: "/logo-indianoil.svg",
      hp: "/logo-hp.svg",
      bpcl: "/logo-bpcl.svg",
      reliance: "/logo-reliance.svg",
      custom: "/brand-logo.png"
    }[template.logoType || "custom"] || "/brand-logo.png";

    const isCustomLogo = !template.logoType || template.logoType === "custom";
    const logoUrlLeft = isCustomLogo ? "/logo-indianoil.svg" : logoUrl;
    const logoUrlRight = "";
    const logoLeftStyle = "";
    const logoRightStyle = "display: none;";
    const logoRightContainerStyle = "display: none;";
    const headerBannerStyle = isCustomLogo ? "" : "justify-content: center;";

    const testPrintData = {
      ...printData,
      logoUrl,
      logoUrlLeft,
      logoUrlRight,
      logoLeftStyle,
      logoRightStyle,
      logoRightContainerStyle,
      headerBannerStyle
    };
    
    // Inject mock data values
    Object.entries(testPrintData).forEach(([key, val]) => {
      const placeholder = new RegExp(`{{${key}}}`, "g");
      html = html.replace(placeholder, String(val));
    });

    return html;
  };

  const getCompiledPreviewHtml = (template: Template) => {
    const compiledBody = compileTemplateHtml(template, MOCK_PRINT_DATA);
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <style>
          * { box-sizing: border-box; }
          body {
            margin: 0;
            padding: 8px;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            font-size: 11px;
            background: #fff;
            color: #1e293b;
            width: 100%;
            height: 100%;
          }
          ${template.styles || ""}
          /* Ensure preview wraps nicely and fits scaled canvas */
          .receipt-container, .invoice-container, .bill-container {
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            border: none !important;
          }
        </style>
      </head>
      <body>
        <div style="width: 100%; height: 100%;">
          ${compiledBody}
        </div>
      </body>
      </html>
    `;
  };

  const handleUpdateActive = async () => {
    if (!selectedBillId || !selectedInvoiceId) {
      toast.warning("Please select one Bill template and one Invoice template");
      return;
    }
    try {
      setSaving(true);
      const logoTypes: Record<string, string> = {};
      templates.forEach(t => {
        logoTypes[t.templateId] = t.logoType || "custom";
      });

      const res = await fetchJSON("/api/print-templates/select", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          billTemplateId: selectedBillId,
          invoiceTemplateId: selectedInvoiceId,
          logoTypes
        })
      });
      const json = await res.json();
      if (json.success) {
        toast.success("Active print templates updated successfully!");
        setTemplates(prev => prev.map(t => {
          if (t.type === "bill") {
            return { ...t, isActive: t.templateId === selectedBillId };
          } else {
            return { ...t, isActive: t.templateId === selectedInvoiceId };
          }
        }));
      } else {
        toast.error(json.error || "Failed to update template configuration");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save changes to the database");
    } finally {
      setSaving(false);
    }
  };

  const handleTestPrint = (template: Template) => {
    const html = compileTemplateHtml(template, MOCK_PRINT_DATA);

    const fullHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Test Print - ${template.name}</title>
        <style>
          @media print {
            body { margin: 0; padding: 0; }
            @page { margin: 0; }
          }
          ${template.styles || ""}
        </style>
      </head>
      <body>
        ${html}
        <script>
          window.onload = function() {
            window.focus();
            setTimeout(function() {
              window.print();
            }, 300);
          }
        </script>
      </body>
      </html>
    `;

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document || iframe.contentDocument;
    if (doc) {
      doc.open();
      doc.write(fullHtml);
      doc.close();

      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 3000);
      toast.info(`Triggered test-print for "${template.name}"`);
    } else {
      toast.error("Failed to compile printing sandboxed viewport");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#3f5cc6] p-6 text-white">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="space-y-2">
            <Skeleton className="h-4 w-32 bg-white/20" />
            <Skeleton className="h-8 w-64 bg-white/20" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Skeleton className="h-96 bg-white/10 rounded-xl" />
            <Skeleton className="h-96 bg-white/10 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (loadError && templates.length === 0) {
    return (
      <div className="min-h-screen bg-[#3f5cc6] p-6 text-white flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md">
          <Printer className="w-12 h-12 text-white/40 mx-auto" />
          <h2 className="text-xl font-bold">Could Not Load Templates</h2>
          <p className="text-white/70 text-sm">{loadError}</p>
          <p className="text-white/50 text-xs">This usually means the server database is still connecting. Please wait a moment and retry.</p>
          <button
            onClick={fetchTemplates}
            className="bg-white/20 hover:bg-white/30 text-white font-bold px-6 py-2 rounded-full text-sm transition"
          >
            ↺ Retry
          </button>
        </div>
      </div>
    );
  }

  const bills = templates.filter(t => t.type === "bill");
  const invoices = templates.filter(t => t.type === "invoice");


  return (
    <div className="min-h-screen bg-[#3b52cd] p-6 text-white font-sans">
      <div className="max-w-[1400px] mx-auto space-y-4">
        
        {/* Breadcrumb */}
        <nav className="text-xs text-white/70 flex items-center gap-1.5" aria-label="Breadcrumb">
          <span className="cursor-pointer hover:text-white flex items-center gap-1">
            <Home className="w-3.5 h-3.5" />
            Dashboard
          </span>
          <ChevronRight className="w-3 h-3 text-white/50" />
          <span className="font-semibold text-white">Print Master</span>
        </nav>

        {/* Header Block & Title */}
        <div className="pb-2">
          <h1 className="text-2xl font-bold tracking-wide">Print Master</h1>
        </div>

        {/* Columns Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Left Column: Bill Prints */}
          <div className="lg:col-span-6 space-y-4">
            <div className="flex items-center justify-between border-b border-white/20 pb-2">
              <h2 className="text-sm font-bold uppercase tracking-wider text-white/90">Bill Prints</h2>
              <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded font-semibold text-white/95">80mm Thermal Receipt</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {bills.map((template) => {
                const isSelected = selectedBillId === template.templateId;
                const imageUrl = TEMPLATE_IMAGES[template.templateId] || "/placeholder.svg";

                return (
                  <div 
                    key={template._id}
                    onClick={() => setSelectedBillId(template.templateId)}
                    className={`relative cursor-pointer bg-white rounded-xl overflow-hidden transition-all duration-300 border-2 shadow-md hover:shadow-lg flex flex-col group ${
                      isSelected 
                        ? "border-[#4caf50] scale-[1.01]" 
                        : "border-transparent hover:border-white/40"
                    }`}
                  >
                    {/* Floating checkmark */}
                    {isSelected && (
                      <div className="absolute top-2 right-2 bg-[#4caf50] text-white rounded-full p-1 z-10 flex items-center justify-center w-6 h-6 shadow animate-fade-in">
                        <Check className="w-4.5 h-4.5 stroke-[3px]" />
                      </div>
                    )}

                    {/* Previews Frame */}
                    <div className="flex-grow bg-slate-50 flex items-center justify-center p-3 h-80 overflow-hidden relative border-b border-slate-100">
                      {TEMPLATE_IMAGES[template.templateId] ? (
                        <img 
                          src={imageUrl} 
                          alt={template.name}
                          className="max-w-full max-h-full object-contain rounded-md shadow-sm transition duration-300 group-hover:scale-[1.02]"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = "/placeholder.svg";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full relative overflow-hidden flex items-center justify-center bg-white rounded-md border border-slate-200/60 shadow-inner">
                          <iframe
                            srcDoc={getCompiledPreviewHtml(template)}
                            title={template.name}
                            className="w-[400px] h-[600px] border-0 pointer-events-none select-none origin-center"
                            style={{
                              transform: "scale(0.5)",
                            }}
                          />
                          <div className="absolute inset-0 bg-transparent" />
                          <span className="absolute bottom-2 left-2 bg-[#3b52cd] text-white font-bold text-[8px] tracking-wide uppercase px-1.5 py-0.5 rounded shadow-sm">
                            Live Preview
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Lower Card Action Bar */}
                    <div className="bg-white px-4 py-3 flex flex-col gap-2">
                      <div className="text-[12px] font-bold text-slate-800 truncate text-center">
                        {template.name}
                      </div>

                      {/* Logo Type Selector */}
                      <div className="flex items-center justify-between w-full gap-2" onClick={(e) => e.stopPropagation()}>
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Logo Style:</span>
                        <select
                          value={template.logoType || "custom"}
                          onChange={(e) => {
                            const newLogoType = e.target.value as any;
                            setTemplates(prev => prev.map(t => 
                              t.templateId === template.templateId 
                                ? { ...t, logoType: newLogoType } 
                                : t
                            ));
                          }}
                          className="text-[11px] bg-slate-50 text-slate-800 border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#3b52cd] font-semibold cursor-pointer"
                        >
                          <option value="indianoil">IndianOil</option>
                          <option value="hp">HP</option>
                          <option value="bpcl">BPCL</option>
                          <option value="reliance">Reliance</option>
                          <option value="custom">Custom/App Logo</option>
                        </select>
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTestPrint(template);
                        }}
                        className="w-full text-center border border-slate-200 rounded py-1 text-[11px] font-bold text-slate-600 bg-slate-50 hover:bg-slate-100 hover:text-slate-800 transition shadow-sm"
                      >
                        Test Print
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Column: Invoice Prints */}
          <div className="lg:col-span-6 space-y-4">
            <div className="flex items-center justify-between border-b border-white/20 pb-2">
              <h2 className="text-sm font-bold uppercase tracking-wider text-white/90">Invoice Prints</h2>
              <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded font-semibold text-white/95">A4 Tax Invoice</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {invoices.map((template) => {
                const isSelected = selectedInvoiceId === template.templateId;
                const imageUrl = TEMPLATE_IMAGES[template.templateId] || "/placeholder.svg";

                return (
                  <div 
                    key={template._id}
                    onClick={() => setSelectedInvoiceId(template.templateId)}
                    className={`relative cursor-pointer bg-white rounded-xl overflow-hidden transition-all duration-300 border-2 shadow-md hover:shadow-lg flex flex-col group ${
                      isSelected 
                        ? "border-[#4caf50] scale-[1.01]" 
                        : "border-transparent hover:border-white/40"
                    }`}
                  >
                    {/* Floating checkmark */}
                    {isSelected && (
                      <div className="absolute top-2 right-2 bg-[#4caf50] text-white rounded-full p-1 z-10 flex items-center justify-center w-6 h-6 shadow animate-fade-in">
                        <Check className="w-4.5 h-4.5 stroke-[3px]" />
                      </div>
                    )}

                    {/* Previews Frame */}
                    <div className="flex-grow bg-slate-50 flex items-center justify-center p-3 h-80 overflow-hidden relative border-b border-slate-100">
                      {TEMPLATE_IMAGES[template.templateId] ? (
                        <img 
                          src={imageUrl} 
                          alt={template.name}
                          className="max-w-full max-h-full object-contain rounded-md shadow-sm transition duration-300 group-hover:scale-[1.02]"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = "/placeholder.svg";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full relative overflow-hidden flex items-center justify-center bg-white rounded-md border border-slate-200/60 shadow-inner">
                          <iframe
                            srcDoc={getCompiledPreviewHtml(template)}
                            title={template.name}
                            className="w-[400px] h-[600px] border-0 pointer-events-none select-none origin-center"
                            style={{
                              transform: "scale(0.4)",
                            }}
                          />
                          <div className="absolute inset-0 bg-transparent" />
                          <span className="absolute bottom-2 left-2 bg-[#3b52cd] text-white font-bold text-[8px] tracking-wide uppercase px-1.5 py-0.5 rounded shadow-sm">
                            Live Preview
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Lower Card Action Bar */}
                    <div className="bg-white px-4 py-3 flex flex-col gap-2">
                      <div className="text-[12px] font-bold text-slate-800 truncate text-center">
                        {template.name}
                      </div>

                      {/* Logo Type Selector */}
                      <div className="flex items-center justify-between w-full gap-2" onClick={(e) => e.stopPropagation()}>
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Logo Style:</span>
                        <select
                          value={template.logoType || "custom"}
                          onChange={(e) => {
                            const newLogoType = e.target.value as any;
                            setTemplates(prev => prev.map(t => 
                              t.templateId === template.templateId 
                                ? { ...t, logoType: newLogoType } 
                                : t
                            ));
                          }}
                          className="text-[11px] bg-slate-50 text-slate-800 border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#3b52cd] font-semibold cursor-pointer"
                        >
                          <option value="indianoil">IndianOil</option>
                          <option value="hp">HP</option>
                          <option value="bpcl">BPCL</option>
                          <option value="reliance">Reliance</option>
                          <option value="custom">Custom/App Logo</option>
                        </select>
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTestPrint(template);
                        }}
                        className="w-full text-center border border-slate-200 rounded py-1 text-[11px] font-bold text-slate-600 bg-slate-50 hover:bg-slate-100 hover:text-slate-800 transition shadow-sm"
                      >
                        Test Print
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

        {/* Global UPDATE Trigger Button */}
        <div className="flex justify-center pt-8 pb-12">
          <button
            onClick={handleUpdateActive}
            disabled={saving}
            className="bg-[#7ce2b8] hover:bg-[#68d0a6] text-slate-900 font-extrabold uppercase text-xs tracking-wider px-12 py-3 rounded-full shadow-lg transition duration-300 active:scale-95 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Check className="w-4 h-4 stroke-[3px]" />
            )}
            Update
          </button>
        </div>

      </div>
    </div>
  );
}
